import React, { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import { useParams } from 'react-router-dom'
import { Box, Card, Typography } from '@mui/material';
import CommonAccountTable from '../../../../documents/components/CommonAccountTable';
import { AccountTableColumn } from '../../../../types';
import { request } from '../../../../../../utils/request';
import { toast } from 'react-toastify';
import PrintButtons from '../../../../../../components/PrintButtons';
import usePrint from '../../../../../../utils/usePrint';
import PrintableContainer from '../../../../../../components/PrintableContainer';

const ShowPaymentOut = () => {
  const { paymentOutId } = useParams();
  const [loading, setLoading] = useState(true);
  const [singlePaymentOut, setSinglePaymentOut] = useState<any | null>({});

  const { isPrinting, handlePrint } = usePrint();

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('payment_out_print', {
          title: `Payment Out - ${singlePaymentOut?.paymentOutNumber}'}`,
          fileName: `Purchase _${singlePaymentOut?.paymentOutNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold; margin-bottom:0px}
          .sub-heading2 {font-size:12px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  const fetchSinglePaymentOut = useCallback(async () => {
    if (!paymentOutId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `paymentOut/get-single-payment-out/${paymentOutId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setSinglePaymentOut(res.data)
      } else {
        toast.error(res.message || "Failed to fetch Payment Out");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching Payment Out");
    } finally {
      setLoading(false)
    }
  }, [paymentOutId])

  useEffect(() => {
    fetchSinglePaymentOut();
  }, [fetchSinglePaymentOut])

  // Define columns for the table
  const columns: AccountTableColumn[] = [
    {
      id: 'paymentOutDate',
      label: 'DATE',
      minWidth: 100,
      renderCell: (row) => {
        if (!row.paymentOutDate) return 'N/A';
        try {
          return new Date(row.paymentOutDate).toLocaleDateString('en-GB');
        } catch (e) {
          return 'Invalid Date';
        }
      }
    },
    {
      id: 'purchaseNumber',
      label: 'PURCHASE NUMBER',
      minWidth: 200,
      renderCell: (row) => {
        if (row.purchaseId && typeof row.purchaseId === 'object') {
          return row.purchaseId.purchaseNumber || '-';
        }
        return '-';
      }
    },
    {
      id: 'totalAmount',
      label: 'PURCHASE AMOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.purchaseId?.totalAmount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'discount',
      label: 'DISCOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.purchaseId?.discount?.amount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'receivedAmount',
      label: 'AMOUNT PAID',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.receivedAmount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'balanceAmount',
      label: 'BALANCE AMOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.purchaseId?.balanceAmount || 0)}
          </span>
        </div>
      )
    }
  ];

  const detailBox = (label: any, value?: any | null) => {
    return (
      <Box flex={4} sx={{ display: 'flex', flexDirection: "column", gap: .8 }}>
        <Typography variant='h6' sx={{ fontSize: '0.78rem', fontWeight: 600 }} className='sub-heading2'>
          {label}
        </Typography>
        <Typography variant='body1' sx={{ fontSize: '0.75rem' }}>
          {value || '--'}
        </Typography>
      </Box>
    )
  }

  // If still loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading Payment Out details...</Typography>
      </Box>
    );
  }

  // If no data found
  if (!singlePaymentOut) {
    return (
      <>
        <DocumentHeader heading='Payment Out' showBackButton={true} />
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography>No payment out data found</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentHeader
        heading='Payment Out'
        subtitle={`#${singlePaymentOut?.paymentOutNumber}`}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>
        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print PO'
          pdfLabel='Export PDF'
          disabled={loading || !singlePaymentOut}
          showPDF={false} // Remove PDF button since we don't have export functionality yet
        />
      </Box>

      {/* Payment Details Section */}
      <PrintableContainer id="payment_out_print">
        <Box mb={4}>
          <Typography variant='h6' mb={1} className='sub-heading'>
            Payment Details
          </Typography>
          <Card sx={{ p: 2, borderRadius: 1.2 }}>
            <Box display='flex' gap={3} mb={3} flexWrap="wrap" className="flex-center">
              {detailBox("Vendor/Party Name", singlePaymentOut?.party?.partyName || singlePaymentOut?.party?.nickName || singlePaymentOut?.vendor?.vendorName || singlePaymentOut?.vendor?.nickName)}
              {detailBox("Payment Date", singlePaymentOut?.paymentOutDate ? new Date(singlePaymentOut.paymentOutDate).toLocaleDateString('en-GB') : '--')}
              {detailBox("Amount Received (₹)", singlePaymentOut?.receivedAmount ?
                new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(singlePaymentOut.receivedAmount)
                : '₹0')}
              {detailBox("Payment Discount (₹)", singlePaymentOut?.discount?.amount ?
                new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(singlePaymentOut.discount.amount)
                : '₹0')}
              {detailBox("Payment Mode", singlePaymentOut?.paymentType)}
              {detailBox("GST Type", singlePaymentOut?.gstType)}
              {/* {detailBox("Settled Amount (₹)", singlePaymentOut?.settledAmount ?
                new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(singlePaymentOut.settledAmount)
                : '₹0')} */}
            </Box>

            {/* Notes Section */}
            <Box mt={2}>
              {detailBox("Notes:", singlePaymentOut?.notes)}
            </Box>
          </Card>
        </Box>

        {/* Invoice Details Section - Using CommonAccountTable */}
        <Box mb={4}>
          <Typography variant='h6' mb={0} className='sub-heading'>
            Purchase Invoice Settled with this Payment
          </Typography>

          {singlePaymentOut ? (
            <CommonAccountTable
              columns={columns}
              data={[singlePaymentOut]} // Wrap single object in array
              loading={false}
              idField='_id'
              getRowLink={(row: any) => `/accounts/purchases/purchaseInvoice/${row?.purchaseId?._id}`}
              paginationEnabled={false}
              showDateRange={false}
              showCreateButton={false}
              showSearchBar={false}
              showFilter={false}
            />
          ) : (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No purchase invoice linked to this payment
              </Typography>
            </Card>
          )}
        </Box>
      </PrintableContainer>

      {/* Payment Summary Stats */}
      {/* <Box mb={4}>
        <Typography variant='h6' mb={2}>
          Payment Summary
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Invoice Amount
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentOut.invoice?.totalAmount || 0)}
            </Typography>
          </Card>
          
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Received
            </Typography>
            <Typography variant="h5" fontWeight={600} color="success.main">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentOut.receivedAmount || 0)}
            </Typography>
          </Card>
          
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Remaining Balance
            </Typography>
            <Typography variant="h5" fontWeight={600} color="warning.main">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentOut.invoice?.balanceAmount || 0)}
            </Typography>
          </Card>
          
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Discount Applied
            </Typography>
            <Typography variant="h5" fontWeight={600} color="info.main">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentOut.discount?.amount || 0)}
            </Typography>
          </Card>
        </Box>
      </Box> */}
    </>
  )
}

export default ShowPaymentOut